import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import { useFormContext, Controller } from 'react-hook-form';

import {
  ReservationFlowMe,
  ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo,
  ReservationFlowSelectedPaymentMethodNewCreditCard,
} from 'src/interfaces/reservation_flow';
import { RSNote } from 'src/reservation-page/RSNote';

import { SectionFieldset } from 'src/organisms/product-purchase/SectionFieldset';
import { PaymentMethodInputNewCreditCard } from 'src/organisms/product-purchase/PaymentMethodInputNewCreditCard';
import { PaymentMethodInput } from 'src/organisms/product-purchase/PaymentMethodInput';
import { SubscriptionPurchaseFormValues } from 'src/pages/[publicId]/subscriptions/[productPublicId]/purchase';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { TokenResponse } from '@mul-pay/mptoken-js';

export interface PaymentMethodFieldsetProps {
  user: ReservationFlowMe;
  stripeErrorMessage?: string;
  isMonthlyBilling: boolean;
  handlePaymentElementChange: (event: StripePaymentElementChangeEvent) => void;
  isMulpayOrderEnabled?: boolean;
  handleMulpayTokenChange: (tokenResponse?: TokenResponse) => void;
  merchantPublicId: string;
}

export const PaymentMethodFieldset: FC<PaymentMethodFieldsetProps> = ({
  user,
  stripeErrorMessage,
  isMonthlyBilling,
  handlePaymentElementChange,
  isMulpayOrderEnabled,
  handleMulpayTokenChange,
  merchantPublicId,
}) => {
  const intl = useIntl();
  const { control } = useFormContext<SubscriptionPurchaseFormValues>();

  // FIXME: yupのバリデーションスキーマに移行する
  const isValidInputPaymentMethodNewCreditCard = (
    paymentMethod: ReservationFlowSelectedPaymentMethodNewCreditCard,
  ) => {
    if (typeof paymentMethod === 'undefined') {
      return false;
    }

    // MEMO: GmoPg, Stripe Intentからはクレジットの入力の値を保存しておらず、入力の完了を感知できないため、常にtrueにしてクレカデータのトークン作成時にvalidationを実施する
    return true;
  };

  // FIXME: yupのバリデーションスキーマに移行する
  const isValidInputPaymentMethod = (
    paymentMethod: ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo,
  ) => {
    if (typeof paymentMethod === 'undefined') {
      return false;
    }
    if (paymentMethod.type !== 'credit_card') {
      return false;
    }
    if (!paymentMethod.is_restore_credit_card) {
      return false;
    }

    return true;
  };

  return (
    <SectionFieldset
      legend={intl.formatMessage({
        defaultMessage: 'お支払い方法',
        id: 'Drticr',
        description:
          'src/templates/merchants/purchase/components/PaymentMethodFieldset/index.tsx',
      })}
    >
      {!!stripeErrorMessage && (
        <div className="mb-2.5">
          <div>
            <RSNote body={stripeErrorMessage} variant="capital"></RSNote>
          </div>
        </div>
      )}

      {(() => {
        if (!user.credit_card) {
          return (
            <Controller
              control={control}
              name="paymentMethod"
              rules={{
                // FIXME: yupのバリデーションスキーマに移行する
                validate: (paymentMethod) => {
                  if (typeof paymentMethod === 'undefined') {
                    return false;
                  }
                  if (paymentMethod.type === 'new_credit_card') {
                    return isValidInputPaymentMethodNewCreditCard(
                      paymentMethod,
                    );
                  }
                  // この分岐には入らないはず
                  return false;
                },
              }}
              render={({ field: { value, onChange, onBlur, ref } }) => {
                return (
                  <PaymentMethodInputNewCreditCard
                    ref={ref}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    isMonthlyBilling={isMonthlyBilling}
                    handlePaymentElementChange={handlePaymentElementChange}
                    isMulpayOrderEnabled={isMulpayOrderEnabled}
                    handleMulpayTokenChange={handleMulpayTokenChange}
                    merchantPublicId={merchantPublicId}
                  />
                );
              }}
            />
          );
        }

        return (
          <Controller
            control={control}
            name="paymentMethod"
            rules={{
              validate: (paymentMethod) => {
                if (typeof paymentMethod === 'undefined') {
                  return false;
                }
                if (paymentMethod.type === 'new_credit_card') {
                  return isValidInputPaymentMethodNewCreditCard(paymentMethod);
                }
                return isValidInputPaymentMethod(paymentMethod);
              },
            }}
            render={({ field: { value, onChange, onBlur, ref } }) => {
              return (
                <PaymentMethodInput
                  ref={ref}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  userCreditCard={user.credit_card}
                  isMonthlyBilling={isMonthlyBilling}
                  handlePaymentElementChange={handlePaymentElementChange}
                  isMulpayOrderEnabled={isMulpayOrderEnabled}
                  handleMulpayTokenChange={handleMulpayTokenChange}
                  merchantPublicId={merchantPublicId}
                />
              );
            }}
          />
        );
      })()}
    </SectionFieldset>
  );
};
